import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CategoryService } from "../services/categoryService";

const ProductCategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

  /* ================= AUTH CHECK ================= */
  useEffect(() => {
    const checkLogin = async () => {
      try {
        const res = await fetch(`${API_BASE}/session`, { credentials: "include" });
        const data = await res.json();
        if (!data.user) navigate("/admin/login");
      } catch {
        navigate("/admin/login");
      }
    };
    checkLogin();
  }, [navigate, API_BASE]);

  /* ================= LOAD CATEGORY (EDIT) ================= */
  useEffect(() => {
    if (!isEditMode || !id) return;

    const cid = parseInt(id);
    if (isNaN(cid)) {
      setMessage("❌ Invalid category id");
      return;
    }

    const loadCategory = async () => {
      try {
        const category = await CategoryService.getCategoryById(cid);
        setName(category.name || "");
        setDescription(category.description || "");
      } catch (err) {
        console.error("Failed to load category:", err);
        setMessage("❌ Failed to load category");
      }
    };

    loadCategory();
  }, [id, isEditMode]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (isEditMode && id) {
        const cid = parseInt(id);
        if (isNaN(cid)) throw new Error("Invalid category id");
        await CategoryService.updateCategory(cid, { name, description });
        setMessage("✅ Category updated successfully!");
      } else {
        await CategoryService.createCategory({ name, description });
        setMessage("✅ Category created successfully!");
        setName("");
        setDescription("");
      }
    } catch (error: any) {
      console.error("Category save error:", error);
      const serverMessage =
        error?.response?.data?.message || "Failed to save category";
      setMessage(`❌ ${serverMessage}`);
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    if (!id) return;
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this category?"
    );
    if (!confirmDelete) return;

    setDeleting(true);
    try {
      await CategoryService.deleteCategory(parseInt(id));
      setMessage("✅ Category deleted successfully!");
      setTimeout(() => navigate("/admin/categories"), 1000);
    } catch (error: any) {
      console.error("Delete error:", error);
      setMessage("❌ Failed to delete category");
    } finally {
      setDeleting(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8 space-y-6">
        <h2 className="text-3xl font-bold text-gray-900 text-center">
          {isEditMode ? "Edit Product Category" : "Create Product Category"}
        </h2>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Name */}
          <div className="flex flex-col">
            <label htmlFor="name" className="mb-2 font-medium text-gray-700">
              Category Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col">
            <label htmlFor="description" className="mb-2 font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-between items-center space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg font-medium transition disabled:opacity-50"
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Category"
                : "Create Category"}
            </button>

            {isEditMode && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>

          {/* Message */}
          {message && (
            <p
              className={`mt-2 text-center text-sm ${
                message.startsWith("✅") ? "text-green-600" : "text-red-600"
              }`}
            >
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProductCategoryForm;
